import { useEffect, useRef, useState } from "react";
import { Camera, Image as ImageIcon, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { MAX_AVATAR_DATA_URL_LENGTH } from "@bookdental/schemas";
import { getPatientColor, patientGradient } from "@/lib/patient-color";
import { cn } from "@/lib/cn";

type Props = {
  value: string | null | undefined;
  onChange: (dataUrl: string | null) => void;
  name?: string;
  /** Tamanho do avatar em pixels (default 80). */
  size?: number;
};

/** Avatar clicável — sem botões externos. Hover mostra ações inline. */
export function PatientAvatarUpload({ value, onChange, name, size = 80 }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione uma imagem");
      return;
    }
    if (file.size > 500_000) {
      toast.error("Imagem muito grande (max ~500KB)");
      return;
    }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setLoading(false);
      if (dataUrl.length > MAX_AVATAR_DATA_URL_LENGTH) {
        toast.error("Imagem muito grande após codificação");
        return;
      }
      onChange(dataUrl);
    };
    reader.onerror = () => {
      setLoading(false);
      toast.error("Falha ao ler o arquivo");
    };
    reader.readAsDataURL(file);
  };

  const initial = (name ?? "?").trim().charAt(0).toUpperCase() || "?";
  const color = getPatientColor(name);
  const sizePx = `${size}px`;

  return (
    <>
      <div className="relative inline-block">
        {/* Avatar — o círculo INTEIRO é o botão de upload */}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          className="group/avatar relative block overflow-hidden rounded-full ring-2 ring-white transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:ring-card"
          style={{
            width: sizePx,
            height: sizePx,
            boxShadow: `0 8px 24px ${color.solid}40`,
          }}
          aria-label="Enviar foto"
        >
          {value ? (
            <img src={value} alt={name ?? ""} className="h-full w-full object-cover" />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center font-bold text-white"
              style={{
                background: patientGradient(color),
                fontSize: `${size * 0.3}px`,
              }}
            >
              {initial}
            </div>
          )}

          {/* Overlay mostra no hover OU quando vazio */}
          <span
            className={cn(
              "absolute inset-0 flex items-center justify-center bg-black/50 text-white transition-opacity",
              value
                ? "opacity-0 group-hover/avatar:opacity-100"
                : "opacity-0 group-hover/avatar:opacity-100",
            )}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <ImageIcon className="h-5 w-5" />
            )}
          </span>
        </button>

        {/* Botão câmera — flutua no canto inferior direito */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setCameraOpen(true);
          }}
          disabled={loading}
          className="absolute -bottom-0.5 -right-0.5 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-brand-600 text-white shadow-md transition-all hover:scale-110 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-card"
          title="Tirar foto com a câmera"
          aria-label="Tirar foto com a câmera"
        >
          <Camera className="h-3.5 w-3.5" />
        </button>

        {/* Botão remover — só aparece se tem foto, no hover */}
        {value && !loading && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="absolute -left-0.5 -top-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-red-500 text-white opacity-0 shadow-md transition-all hover:scale-110 hover:bg-red-600 group-hover/avatar:opacity-100 dark:border-card"
            title="Remover foto"
            aria-label="Remover foto"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = "";
          }}
        />
      </div>

      {cameraOpen && (
        <CameraCaptureModal
          onCapture={(dataUrl) => {
            onChange(dataUrl);
            setCameraOpen(false);
          }}
          onClose={() => setCameraOpen(false)}
        />
      )}
    </>
  );
}

// ─── Câmera modal ────────────────────────────────────────

function CameraCaptureModal({
  onCapture,
  onClose,
}: {
  onCapture: (dataUrl: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: 640, height: 480 },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (e) {
        setError(
          e instanceof DOMException && e.name === "NotAllowedError"
            ? "Acesso à câmera negado. Permita nas configurações do navegador."
            : "Não foi possível acessar a câmera.",
        );
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  const capture = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    // Ratio quadrado — corta do centro pra combinar com avatar circular
    const side = Math.min(w, h);
    canvas.width = side;
    canvas.height = side;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(
      video,
      (w - side) / 2,
      (h - side) / 2,
      side,
      side,
      0,
      0,
      side,
      side,
    );
    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    if (dataUrl.length > MAX_AVATAR_DATA_URL_LENGTH) {
      toast.error("Foto muito grande. Tente novamente.");
      return;
    }
    onCapture(dataUrl);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-3 dark:border-border">
          <h3 className="text-sm font-bold text-zinc-900 dark:text-foreground">
            Tirar foto
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 dark:hover:bg-muted"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative aspect-square bg-zinc-950">
          {error ? (
            <div className="flex h-full items-center justify-center p-6 text-center text-sm text-zinc-300">
              {error}
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="h-full w-full object-cover"
                style={{ transform: "scaleX(-1)" }}
              />
              {!ready && (
                <div className="absolute inset-0 flex items-center justify-center text-white">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 p-4">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 items-center rounded-md border border-zinc-200 bg-white px-3.5 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={capture}
            disabled={!ready || !!error}
            className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
          >
            <Camera className="h-3.5 w-3.5" />
            Capturar foto
          </button>
        </div>
      </div>
    </div>
  );
}
